import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, Plus, ChevronLeft, ChevronRight, Users, Clock, 
  UserPlus, UserMinus, Trash2, Edit, AlertCircle, Check, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

import { API_URL } from '@/config/api';

const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const statusConfig = {
  open: { color: 'bg-slate-100 text-slate-600', label: 'Open' },
  filled: { color: 'bg-green-100 text-green-700', label: 'Filled' },
  understaffed: { color: 'bg-amber-100 text-amber-700', label: 'Understaffed' },
  cancelled: { color: 'bg-red-100 text-red-700', label: 'Cancelled' }
};

const roleColors = {
  kitchen: 'bg-orange-100 text-orange-700',
  operations_manager: 'bg-blue-100 text-blue-700',
  customer_support: 'bg-purple-100 text-purple-700',
  finance: 'bg-green-100 text-green-700',
  manager: 'bg-indigo-100 text-indigo-700'
};

export default function AdminStaffScheduling() {
  const { token } = useAuth();
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1);
    return monday.toISOString().split('T')[0];
  });
  const [schedule, setSchedule] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isShiftDialogOpen, setIsShiftDialogOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [shiftForm, setShiftForm] = useState({
    name: 'Morning',
    start_time: '06:00',
    end_time: '14:00',
    role_required: 'kitchen',
    min_staff: '1',
    max_staff: '3',
    notes: ''
  });

  useEffect(() => {
    fetchWeeklySchedule();
  }, [token, weekStart]);

  const fetchWeeklySchedule = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/schedule/week?week_start=${weekStart}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSchedule(data.schedule);
        setStaff(data.staff);
      }
    } catch (error) {
      console.error('Error fetching schedule:', error);
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (direction) => {
    const current = new Date(weekStart);
    current.setDate(current.getDate() + (direction * 7));
    setWeekStart(current.toISOString().split('T')[0]);
  };

  const openCreateShift = (day) => {
    setSelectedDay(day);
    setEditingShift(null);
    setShiftForm({
      name: 'Morning',
      start_time: '06:00',
      end_time: '14:00',
      role_required: 'kitchen',
      min_staff: '1',
      max_staff: '3',
      notes: ''
    });
    setIsShiftDialogOpen(true);
  };

  const openEditShift = (shift) => {
    setEditingShift(shift);
    setShiftForm({
      name: shift.name || 'Morning',
      start_time: shift.start_time || '06:00',
      end_time: shift.end_time || '14:00',
      role_required: shift.role_required || 'kitchen',
      min_staff: shift.min_staff?.toString() || '1',
      max_staff: shift.max_staff?.toString() || '3',
      notes: shift.notes || ''
    });
    setIsShiftDialogOpen(true);
  };

  const handleSubmitShift = async (e) => {
    e.preventDefault();
    
    try {
      if (editingShift) {
        // Update existing shift
        await fetch(`${API_URL}/api/admin/shifts/${editingShift.shift_id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include',
          body: JSON.stringify({
            ...shiftForm,
            min_staff: parseInt(shiftForm.min_staff),
            max_staff: parseInt(shiftForm.max_staff)
          })
        });
        toast.success('Shift updated');
      } else {
        // Create new shift
        const dayData = schedule[selectedDay];
        await fetch(`${API_URL}/api/admin/shifts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include',
          body: JSON.stringify({
            ...shiftForm,
            date: dayData.date,
            min_staff: parseInt(shiftForm.min_staff),
            max_staff: parseInt(shiftForm.max_staff)
          })
        });
        toast.success('Shift created');
      }
      
      setIsShiftDialogOpen(false);
      fetchWeeklySchedule();
    } catch (error) {
      toast.error('Failed to save shift');
    }
  };

  const deleteShift = async (shiftId) => {
    if (!confirm('Delete this shift?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/shifts/${shiftId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      toast.success('Shift deleted');
      fetchWeeklySchedule();
    } catch (error) {
      toast.error('Failed to delete shift');
    }
  };

  const assignStaff = async (shiftId, userId) => {
    try {
      await fetch(`${API_URL}/api/admin/shifts/${shiftId}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ user_id: userId })
      });
      toast.success('Staff assigned');
      fetchWeeklySchedule();
    } catch (error) {
      toast.error('Failed to assign staff');
    }
  };

  const unassignStaff = async (shiftId, userId) => {
    try {
      await fetch(`${API_URL}/api/admin/shifts/${shiftId}/unassign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ user_id: userId })
      });
      toast.success('Staff removed');
      fetchWeeklySchedule();
    } catch (error) {
      toast.error('Failed to remove staff');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="space-y-6" data-testid="admin-staff-scheduling">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Calendar className="h-6 w-6 text-primary" />
            Staff Scheduling
          </h1>
          <p className="text-slate-500 mt-1">Manage shifts and staff assignments</p>
        </div>
      </div>

      {/* Week Navigation */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={() => navigateWeek(-1)}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous Week
            </Button>
            <div className="text-center">
              <p className="font-semibold text-lg">
                Week of {formatDate(weekStart)}
              </p>
              <p className="text-sm text-slate-500">
                {staff.length} staff members available
              </p>
            </div>
            <Button variant="outline" onClick={() => navigateWeek(1)}>
              Next Week
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Schedule Grid */}
      {loading ? (
        <div className="grid grid-cols-7 gap-4">
          {DAYS.map(day => (
            <div key={day} className="h-64 bg-slate-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-3">
          {DAYS.map((day, idx) => {
            const dayData = schedule?.[day];
            const isToday = dayData?.date === new Date().toISOString().split('T')[0];
            
            return (
              <div
                key={day}
                className={`bg-white rounded-xl border-2 overflow-hidden min-h-[300px] ${
                  isToday ? 'border-primary' : 'border-slate-200'
                }`}
              >
                {/* Day Header */}
                <div className={`px-3 py-2 text-center ${isToday ? 'bg-primary text-white' : 'bg-slate-50'}`}>
                  <p className="font-semibold">{DAY_NAMES[idx]}</p>
                  <p className={`text-sm ${isToday ? 'text-white/80' : 'text-slate-500'}`}>
                    {dayData ? formatDate(dayData.date) : ''}
                  </p>
                </div>

                {/* Shifts */}
                <div className="p-2 space-y-2">
                  <AnimatePresence>
                    {dayData?.shifts.map((shift) => (
                      <motion.div
                        key={shift.shift_id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="bg-slate-50 rounded-lg p-2 text-xs"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold">{shift.name}</span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openEditShift(shift)}
                              className="p-1 hover:bg-slate-200 rounded"
                            >
                              <Edit className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => deleteShift(shift.shift_id)}
                              className="p-1 hover:bg-red-100 rounded text-red-500"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-slate-500 mb-1">
                          <Clock className="h-3 w-3" />
                          {shift.start_time} - {shift.end_time}
                        </div>
                        <Badge className={`${roleColors[shift.role_required] || 'bg-slate-100'} text-xs`}>
                          {shift.role_required}
                        </Badge>
                        
                        {/* Assigned Staff */}
                        <div className="mt-2 space-y-1">
                          {shift.assigned_staff_details?.map((s) => (
                            <div key={s.user_id} className="flex items-center justify-between bg-white rounded px-1 py-0.5">
                              <span className="truncate">{s.name || s.email}</span>
                              <button
                                onClick={() => unassignStaff(shift.shift_id, s.user_id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                        
                        {/* Status & Add Staff */}
                        <div className="mt-2 flex items-center justify-between">
                          <Badge className={statusConfig[shift.status]?.color || statusConfig.open.color}>
                            {shift.assigned_staff?.length || 0}/{shift.min_staff}
                          </Badge>
                          {(shift.assigned_staff?.length || 0) < shift.max_staff && (
                            <Select onValueChange={(userId) => assignStaff(shift.shift_id, userId)}>
                              <SelectTrigger className="w-auto h-6 text-xs">
                                <UserPlus className="h-3 w-3" />
                              </SelectTrigger>
                              <SelectContent>
                                {staff
                                  .filter(s => s.role === shift.role_required && !shift.assigned_staff?.includes(s.user_id))
                                  .map((s) => (
                                    <SelectItem key={s.user_id} value={s.user_id}>
                                      {s.name || s.email}
                                    </SelectItem>
                                  ))
                                }
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  {/* Add Shift Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full border-dashed border-2 border-slate-200 text-slate-400 hover:border-primary hover:text-primary"
                    onClick={() => openCreateShift(day)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Shift
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Shift Dialog */}
      <Dialog open={isShiftDialogOpen} onOpenChange={setIsShiftDialogOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{editingShift ? 'Edit Shift' : 'Create Shift'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitShift} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Shift Name</Label>
                <Select value={shiftForm.name} onValueChange={(v) => setShiftForm(p => ({ ...p, name: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Morning">Morning</SelectItem>
                    <SelectItem value="Afternoon">Afternoon</SelectItem>
                    <SelectItem value="Evening">Evening</SelectItem>
                    <SelectItem value="Full Day">Full Day</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Role Required</Label>
                <Select value={shiftForm.role_required} onValueChange={(v) => setShiftForm(p => ({ ...p, role_required: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kitchen">Kitchen</SelectItem>
                    <SelectItem value="operations_manager">Operations</SelectItem>
                    <SelectItem value="customer_support">Support</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={shiftForm.start_time}
                  onChange={(e) => setShiftForm(p => ({ ...p, start_time: e.target.value }))}
                />
              </div>
              <div>
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={shiftForm.end_time}
                  onChange={(e) => setShiftForm(p => ({ ...p, end_time: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Min Staff</Label>
                <Input
                  type="number"
                  min="1"
                  value={shiftForm.min_staff}
                  onChange={(e) => setShiftForm(p => ({ ...p, min_staff: e.target.value }))}
                />
              </div>
              <div>
                <Label>Max Staff</Label>
                <Input
                  type="number"
                  min="1"
                  value={shiftForm.max_staff}
                  onChange={(e) => setShiftForm(p => ({ ...p, max_staff: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Input
                value={shiftForm.notes}
                onChange={(e) => setShiftForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Optional notes..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsShiftDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-primary hover:bg-primary-600">
                {editingShift ? 'Update' : 'Create'} Shift
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
